import axios from 'axios';

type LoginCallback = () => void;
type LogoutCallback = () => void;

const unauthenticatedRequest = axios.create();
class AuthService {
  private token = '';
  private onLogin: LoginCallback = () => {};
  private onLogout: LogoutCallback = () => {};
  private fetchTokenPromise: Promise<any> | undefined;
  private tokenRefreshPromise: Promise<any> | undefined;

  constructor() {
    this.checkForSession();
  }

  public async checkForSession() {
    if (this.token) {
      return true;
    }

    try {
      const { data } = await this.fetchToken();
      this.updateToken(data.token);
      return true;
    } catch ({ response }) {
      return false;
    }
  }

  public isAuthenticated() {
    return !!this.token;
  }

  public getToken() {
    return this.token;
  }

  public async pingSessionHeartbeat() {
    try {
      const { data } = await unauthenticatedRequest.get('clients/account-settings/heartbeat');
      return { hasSession: true, expiresAt: data.expires_at };
    } catch (error: any) {
      if (error.response?.status === 401) {
        return { hasSession: false };
      }
      throw error;
    }
  }

  public async refreshToken() {
    if (this.tokenRefreshPromise) { return this.tokenRefreshPromise; }

    try {
      this.tokenRefreshPromise = this.fetchToken();
      const response = await this.tokenRefreshPromise;
      this.token = response.data.token;
    } catch (error: any) {
      if (error.response?.status === 401) {
        this.token = '';
        await this.signOut();
      }
    } finally {
      delete this.tokenRefreshPromise;
      return this.token;
    }
  }

  // AuthContext needs to be notified when a user successfully signs in.
  // NOTE: This method is only for configuring the context. Use values
  // provided by AuthContext instead.
  public appLoginCallback(cb: LoginCallback) {
    this.onLogin = cb;

    // If we're already authenticated, go ahead and trigger the callback:
    if (this.isAuthenticated()) {
      cb();
    }
  }

  // AuthContext needs to be notified when we fail to find a session.
  // NOTE: This method is only for configuring the context. Use values
  // provided by AuthContext instead.
  public appLogoutCallback(cb: LogoutCallback) {
    this.onLogout = cb;
  }

  public async signOut() {
    if (this.token) {
      try {
        await unauthenticatedRequest.delete('/clients/account-settings/logout');
      } catch (error: any) {
        // Nothing to do here.
      } finally {
        this.token = '';
      }
    }
    this.onLogout();
  }

  private async fetchToken() {
    if (this.fetchTokenPromise) { return this.fetchTokenPromise; }
    try {
      this.fetchTokenPromise = unauthenticatedRequest.get('/clients/account-settings/token');
      return await this.fetchTokenPromise;
    } finally {
      delete this.fetchTokenPromise;
    }
  }

  private updateToken(token: string) {
    this.token = token;

    if (token) {
      this.onLogin();
    }
  }
}

const auth = new AuthService();

export default auth;
